import { useEffect, useState, useRef } from 'react';
import api from './services/api';
import './App.css';
import TopMenu from './components/TopMenu';
import LoginPage from './pages/LoginPage';
import RegisterPage from './pages/RegisterPage';
import BrowsePage from './pages/BrowsePage';
import ContentFormPage from './pages/ContentFormPage';
import TakeTestPage from './pages/TakeTestPage';
import QuestionPage from './pages/QuestionPage';
import RBACPage from './pages/RBACPage';
import CollegesPage from './pages/CollegesPage';
import ForgotPasswordPage from './pages/ForgotPasswordPage';
import ResetPasswordPage from './pages/ResetPasswordPage';
import VerifyEmailPage from './pages/VerifyEmailPage';
import ContactPage from './pages/ContactPage';
import AboutPage from './pages/AboutPage';
import DocumentViewerPage from './pages/DocumentViewerPage';

const PAGE_METADATA = {
  browse: {
    title: 'Study Centro - Indian Education Study Materials & Notes',
    description: 'Access textbooks, study notes, syllabus, and past exam papers for Indian universities, colleges, and schools.'
  },
  test: {
    title: 'Practice Tests & Mock Exams | Study Centro',
    description: 'Solve practice MCQs, mock tests, and previous year question papers for CBSE, ICSE, JEE, NEET, and Indian university examinations.'
  },
  colleges: {
    title: 'Indian Colleges & Syllabus Database | Study Centro',
    description: 'Explore and navigate study materials organized by major Indian colleges, universities, and departments.'
  },
  about: {
    title: 'About Study Centro - Learning Platform for India',
    description: 'Learn about Study Centro\'s mission to digitize and share learning resources across the Indian educational landscape.'
  },
  contact: {
    title: 'Contact Study Centro Support',
    description: 'Get in touch with Study Centro for queries, support, or feedback on Indian education study resources.'
  },
  login: {
    title: 'Login | Study Centro',
    description: 'Sign in to access your customized Indian education study planner, saved files, and practice test progress.'
  },
  register: {
    title: 'Sign Up & Join Study Centro',
    description: 'Create an account to contribute study resources, solve practice exams, and connect with other students.'
  },
  create: {
    title: 'Contribute Study Resources | Study Centro',
    description: 'Add or edit notes, syllabus info, or question banks to support fellow students in the Indian education system.'
  },
  edit: {
    title: 'Contribute Study Resources | Study Centro',
    description: 'Add or edit notes, syllabus info, or question banks to support fellow students in the Indian education system.'
  },

  rbac: {
    title: 'Access Control & Moderation | Study Centro',
    description: 'Manage community moderator permissions and roles on Study Centro.'
  },
  'forgot-password': {
    title: 'Account Services | Study Centro',
    description: 'Securely recover or verify your Study Centro account.'
  },
  'reset-password': {
    title: 'Account Services | Study Centro',
    description: 'Securely recover or verify your Study Centro account.'
  },
  'verify-email': {
    title: 'Account Services | Study Centro',
    description: 'Securely recover or verify your Study Centro account.'
  },
  document: {
    title: 'View Document | Study Centro',
    description: 'Read and view study documents, notes, and study material on Study Centro.'
  }
};


function App() {
  const guestUser = { name: 'Guest', role: 'guest', colleges: [], subjects: [] };
  const [user, setUser] = useState(null);
  const [view, setView] = useState('browse');
  const [activeExamId, setActiveExamId] = useState(null);
  const [activeQuestionId, setActiveQuestionId] = useState(null);
  const [activeDocumentId, setActiveDocumentId] = useState(null);
  const [activeToken, setActiveToken] = useState(null);
  const [editItem, setEditItem] = useState(null);
  const [initialFolderId, setInitialFolderId] = useState('');
  const [browseInitialFolderId, setBrowseInitialFolderId] = useState('');
  const [theme, setTheme] = useState(localStorage.getItem('studymate_theme') || 'indigo');

  const isTestActiveRef = useRef(false);
  const activeExamIdRef = useRef(null);

  useEffect(() => {
    activeExamIdRef.current = activeExamId;
  }, [activeExamId]);

  const handleTestActiveChange = (active) => {
    isTestActiveRef.current = active;
  };

  // Global beforeunload listener to prevent refresh/tab close during test
  useEffect(() => {
    const handleBeforeUnloadGlobal = (e) => {
      if (isTestActiveRef.current) {
        e.preventDefault();
        const message = 'Are you sure you want to leave? Your progress will be lost.';
        e.returnValue = message;
        return message;
      }
    };
    window.addEventListener('beforeunload', handleBeforeUnloadGlobal);
    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnloadGlobal);
    };
  }, []);

  // Helper to change URL and trigger custom state routing updates
  const navigateToPath = (path, replace = false) => {
    if (replace) {
      window.history.replaceState({}, '', path);
    } else {
      window.history.pushState({}, '', path);
    }
    // Dispatch a popstate event to let our listener update the state
    window.dispatchEvent(new PopStateEvent('popstate'));
  };

  // ── Sync View with URL Pathname ──
  useEffect(() => {
    const handlePathChange = () => {
      const path = window.location.pathname;
      const parts = path.split('/').filter(Boolean);
      let v = parts[0];
      const idOrToken = parts[1];

      // Intercept navigation away from active test
      if (isTestActiveRef.current) {
        const isSameTest = (v === 'test' && idOrToken === activeExamIdRef.current);
        if (!isSameTest) {
          // Push the active test URL back into history
          window.history.pushState(null, '', `/test/${activeExamIdRef.current}`);
          // Dispatch custom event to trigger exit warning modal in TakeTestPage
          window.dispatchEvent(new CustomEvent('trigger-test-exit-confirm'));
          return;
        }
      }

      // Auto-redirect root "/" to "/browse" without adding to history
      if (!v) {
        v = 'browse';
        window.history.replaceState({}, '', '/browse');
      }

      if (v === 'question' && idOrToken && parts[2]) {
        setActiveExamId(idOrToken);
        setActiveQuestionId(parts[2]);
        setView('question');
      } else if (v === 'test' && idOrToken) {
        setActiveExamId(idOrToken);
        setView('test');
      } else if (v === 'document' && idOrToken) {
        setActiveDocumentId(idOrToken);
        setView('document');
      } else if (v === 'verify-email' && idOrToken) {
        setActiveToken(idOrToken);
        setView('verify-email');
      } else if (v === 'reset-password' && idOrToken) {
        setActiveToken(idOrToken);
        setView('reset-password');
      } else if (v === 'edit' && idOrToken) {
        // /edit/:examId or /edit/:documentId — fetch and open the editor
        setView('edit');
        api.get(`/exams/${idOrToken}`)
          .then(res => {
            setEditItem({ type: 'exam', item: res.data });
          })
          .catch(() => {
            api.get(`/documents/${idOrToken}`)
              .then(res => {
                setEditItem({ type: 'document', item: res.data });
              })
              .catch(() => {
                setView('browse');
              });
          });
      } else if (['browse', 'rbac', 'colleges', 'login', 'register', 'create', 'edit', 'forgot-password', 'contact', 'about', 'document'].includes(v)) {
        setView(v);
        if (v === 'browse') setBrowseInitialFolderId(idOrToken || '');
      } else {
        setView('browse');
        setBrowseInitialFolderId('');
      }
    };

    window.addEventListener('popstate', handlePathChange);
    handlePathChange(); // initial load
    return () => {
      window.removeEventListener('popstate', handlePathChange);
    };
  }, []);

  useEffect(() => {
    document.body.className = theme === 'indigo' ? '' : `theme-${theme}`;
    localStorage.setItem('studymate_theme', theme);
  }, [theme]);

  // ── Sync SEO Title and Meta Tags with Active View ──
  useEffect(() => {
    const meta = PAGE_METADATA[view] || {
      title: 'Study Centro - Your Ultimate Exam Preparation Platform',
      description: 'Prepare for exams with ease. Access documents, textbooks, and practice tests in one place.'
    };

    // Update document title
    document.title = meta.title;

    // Update meta description
    let metaDesc = document.querySelector('meta[name="description"]');
    if (metaDesc) {
      metaDesc.setAttribute('content', meta.description);
    } else {
      metaDesc = document.createElement('meta');
      metaDesc.setAttribute('name', 'description');
      metaDesc.setAttribute('content', meta.description);
      document.head.appendChild(metaDesc);
    }

    // Update Open Graph (OG) tags
    const ogTitle = document.querySelector('meta[property="og:title"]');
    if (ogTitle) ogTitle.setAttribute('content', meta.title);

    const ogDesc = document.querySelector('meta[property="og:description"]');
    if (ogDesc) ogDesc.setAttribute('content', meta.description);

    // Update Twitter tags
    const twitterTitle = document.querySelector('meta[property="twitter:title"]');
    if (twitterTitle) twitterTitle.setAttribute('content', meta.title);

    const twitterDesc = document.querySelector('meta[property="twitter:description"]');
    if (twitterDesc) twitterDesc.setAttribute('content', meta.description);
  }, [view]);

  useEffect(() => {
    const saved = localStorage.getItem('studymate_user');
    if (saved) {
      try {
        const parsed = JSON.parse(saved);
        if (parsed) {
          setUser(parsed);
          if (window.location.pathname === '/' || window.location.pathname === '') {
            navigateToPath('/browse', true);
          }
        }
      } catch (e) {
        console.error('Failed to parse saved user:', e);
        localStorage.removeItem('studymate_user');
      }
    }
  }, []);

  const handleLogin = (userData) => {
    setUser(userData);
    navigateToPath('/browse');
  };

  const handleLogout = () => {
    localStorage.removeItem('studymate_token');
    localStorage.removeItem('studymate_user');
    setUser(null);
    navigateToPath('/browse');
  };

  const navigateTo = (newView) => {
    navigateToPath(`/${newView}`);
  };

  const handleTakeTest = (examId) => {
    navigateToPath(`/test/${examId}`);
  };

  const handleViewDocument = (documentId) => {
    navigateToPath(`/document/${documentId}`);
  };

  const handleEditContent = (item) => {
    setEditItem(item);
    navigateToPath(`/edit/${item.item._id}`);
  };

  const handleCreateAndEditContent = (item) => {
    setEditItem(item);
    navigateToPath(`/edit/${item.item._id}`, true);
  };

  const handleCreateContent = (folderId = '') => {
    setEditItem(null);
    setInitialFolderId(folderId);
    navigateToPath('/create');
  };

  const currentUser = user || guestUser;

  // Render auth screens without the shell
  if (['login', 'register', 'forgot-password', 'reset-password', 'verify-email'].includes(view)) {
    return (
      <>
        {view === 'login' && (
          <LoginPage 
            onLogin={handleLogin} 
            switchToRegister={() => navigateTo('register')} 
            switchToForgot={() => navigateTo('forgot-password')}
            onGuestAccess={() => navigateTo('browse')}
          />
        )}
        {view === 'register' && <RegisterPage switchToLogin={() => navigateTo('login')} />}
        {view === 'forgot-password' && <ForgotPasswordPage switchToLogin={() => navigateTo('login')} />}
        {view === 'reset-password' && <ResetPasswordPage token={activeToken} switchToLogin={() => navigateTo('login')} />}
        {view === 'verify-email' && <VerifyEmailPage token={activeToken} switchToLogin={() => navigateTo('login')} />}
      </>
    );
  }

  // Common Layout Shell for all other pages
  return (
    <div className="app-shell">
      {view !== 'test' && view !== 'edit' && (
        <TopMenu
          user={currentUser}
          activeView={view}
          onLogout={user ? handleLogout : () => navigateTo('login')}
          onGoToBrowse={() => navigateTo('browse')}
          onGoToRBAC={() => navigateTo('rbac')}
          onGoToColleges={() => navigateTo('colleges')}
          onGoToContact={() => navigateTo('contact')}
          onGoToAbout={() => navigateTo('about')}
          onCreateContent={user ? handleCreateContent : null}
          theme={theme}
          onThemeChange={setTheme}
        />
      )}

      <main className="app-main">
        {view === 'browse' && (
          <BrowsePage
            user={currentUser}
            onLogout={user ? handleLogout : () => navigateTo('login')}
            onTakeTest={handleTakeTest}
            onViewDocument={handleViewDocument}
            onCreateContent={handleCreateContent}
            onEditContent={handleEditContent}
            initialFolderId={browseInitialFolderId}
            onFolderSelect={(id) => {
              setBrowseInitialFolderId(id);
              navigateToPath(id ? `/browse/${id}` : '/browse');
            }}
            onGoToRBAC={() => navigateTo('rbac')}
          />
        )}

        {view === 'test' && activeExamId && (
          <TakeTestPage
            examId={activeExamId}
            onBack={() => window.history.back()}
            user={currentUser}
            onLogout={user ? handleLogout : () => navigateTo('login')}
            onGoToBrowse={() => navigateTo('browse')}
            theme={theme}
            onThemeChange={setTheme}
            onTestActiveChange={handleTestActiveChange}
          />
        )}

        {view === 'question' && activeExamId && activeQuestionId && (
          <QuestionPage
            examId={activeExamId}
            questionId={activeQuestionId}
            onEditExam={() => navigateToPath(`/edit/${activeExamId}`)}
            user={currentUser}
            onLogout={user ? handleLogout : () => navigateTo('login')}
            onGoToBrowse={() => navigateTo('browse')}
            theme={theme}
            onThemeChange={setTheme}
          />
        )}

        {view === 'document' && activeDocumentId && (
          <DocumentViewerPage
            documentId={activeDocumentId}
            onBack={() => window.history.back()}
            user={currentUser}
          />
        )}

        {user && (view === 'create' || view === 'edit') && (
          <div className="app-main-centered" style={{ padding: '24px' }}>
            <div style={{ width: '100%', maxWidth: '1200px' }}>
              <ContentFormPage
                user={user}
                editItem={view === 'edit' ? editItem : null}
                initialFolderId={initialFolderId}
                onBack={() => window.history.back()}
                onLogout={handleLogout}
                onGoToBrowse={() => navigateTo('browse')}
                onCreatedItem={handleCreateAndEditContent}
                theme={theme}
                onThemeChange={setTheme}
              />
            </div>
          </div>
        )}


        {user && view === 'rbac' && (
          <div className="app-main-centered">
            <RBACPage user={user} onGoToColleges={() => navigateTo('colleges')} />
          </div>
        )}

        {user && view === 'colleges' && (
          <div className="app-main-centered">
            <CollegesPage user={user} onGoToRBAC={() => navigateTo('rbac')} />
          </div>
        )}

        {view === 'contact' && (
          <div className="app-main-centered">
            <ContactPage onBack={() => window.history.back()} user={user} />
          </div>
        )}

        {view === 'about' && (
          <div className="app-main-centered">
            <AboutPage onBack={() => navigateTo('browse')} />
          </div>
        )}

        {!user && ['rbac', 'colleges', 'create', 'edit'].includes(view) && (
          <div className="app-main-centered">
            <div className="page-card access-state">
              <h2>Sign in required</h2>
              <p>You need an account before you can open this workspace area.</p>
              <div className="action-row">
                <button type="button" onClick={() => navigateTo('login')}>Login</button>
                <button type="button" className="secondary" onClick={() => navigateTo('browse')}>Back to Browse</button>
              </div>
            </div>
          </div>
        )}
      </main>
    </div>
  );
}

export default App;
